import { UnknownAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { CarInformation, InitialData, StepData, StepDataData } from '../Types/FitAssistantData';
import { FitAssistantProductsResponse, GetProductsQuery } from '../Types/FitAssistantProductsResponse';
import {
  SetFitStatusForAllProducts,
  SetFitStatusForConfirmedFits,
  isConfirmedNoFit,
  setSolutionProductListItemImage,
} from '../../FitAssistantShared/Functions';
import { setFitStatus, setShowFitAssistant, setShowResetButtons, setStepEnum } from '../Slices/FitAssistantSlice';
import { addMatchingProducts } from '../Slices/MatchingProductsSlice';
import { Constants } from '../Constants';
import { IsProductPage } from '../../FitAssistantShared/IsProductPage';
import { SelectedCarFromCookie } from '../Thunks/Types';
import { trackFit } from '../Analytics/Adobe';
import { ProductPageHelper } from './ProductPageHelper';
import { MatchingProduct } from '../Types/MatchingProducts';
import { setPricePLP } from '../Util/ProductListUtils';
import {
  handleDocuments,
  setAddToCartBtnForDBG,
  setImageBasedOnRoofRack,
  UpdateFitAssistantPriceAndATCForPDP,
  UpdateFitAssistantPriceAndATCForPDPExtranet,
} from '../Util/ProductPageUtils';
import FitAssistantStatus from '../Enums/FitAssistantStatus';
import FitAssistantStep from '../Enums/FitAssistantStep';
import FitAssistantRooftopTentStatus from '../Enums/FitAssistantRooftopTentStatus';
import FitAssistantVariation from '../Enums/FitAssistantVariation';
import { CookieHelper } from './CookieHelper';
import { getPerCategorySearchString, getSearchString } from '../Util/SearchString';
import { setQueryFromSearchParameters } from '../Util/QueryFromParameters';
import { onFitAssistantSaveData } from '../Analytics/Google';

export class ProductLoader {
  data: GetProductsQuery;
  selectedCar: CarInformation[];
  selectedCarFromCookie: SelectedCarFromCookie | null;
  setCookie: boolean;
  initialData: InitialData;
  stepData: StepData[];
  shouldTriggerTracking: boolean;
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
  response: FitAssistantProductsResponse;

  constructor(
    data: GetProductsQuery,
    selectedCar: CarInformation[],
    selectedCarFromCookie: SelectedCarFromCookie | null,
    setCookie: boolean,
    initialData: InitialData,
    stepData: StepData[],
    shouldTriggerTracking: boolean,
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  ) {
    this.data = data;
    this.selectedCar = selectedCar;
    this.selectedCarFromCookie = selectedCarFromCookie;
    this.setCookie = setCookie;
    this.initialData = initialData;
    this.stepData = stepData;
    this.shouldTriggerTracking = shouldTriggerTracking;
    this.dispatch = dispatch;

    this.response = data.getCarSelectorProducts.carSelectorProductsResponse;
  }

  public Load(): void {
    if (this.data && this.response) {
      if (this.initialData.FitAssistantVariation === FitAssistantVariation.RoofPlatforms) {
        this.HandlePlatform();
      } else if (this.initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents) {
        this.HandleRooftopTents();
      } else if (this.initialData.FitAssistantVariation === FitAssistantVariation.CarSeats) {
        this.HandleCarSeats();
      } else {
        this.HandleDefault();
      }
    }
  }

  public HandleSelectListClick(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
          this.dispatch(setShowResetButtons(true));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        this.dispatch(setShowResetButtons(true));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      if (this.initialData.FitAssistantVariation === FitAssistantVariation.CarSeats) {
        this.dispatch(setStepEnum(FitAssistantStep.CarSeatFit));
      } else {
        this.dispatch(setShowFitAssistant(false));
        this.dispatch(setShowResetButtons(true));
      }
      trackFit(
        this.initialData.FitAssistantVariation,
        this.initialData.IsYearFirst,
        this.selectedCar,
        this.shouldTriggerTracking
      );
    }
  }

  private HandlePlatform(): void {
    let platformProducts = this.response.MatchingProducts?.filter(
      (x) => x.ObjectTypeId === Constants.SolutionTypes.PLATFORM
    );

    if (platformProducts?.length > 0) {
      const fitStatus: FitAssistantStatus = this.SetCompatibility();

      this.dispatch(addMatchingProducts(this.data));

      if (fitStatus === FitAssistantStatus.Confirmed) {
        let stepsNotIncludingRoofRack = this.selectedCar.filter(
          (car) => car.parameterName !== FitAssistantStep.RoofRacks
        );
        let stepToShow = stepsNotIncludingRoofRack[stepsNotIncludingRoofRack.length - 1].parameterName;
        this.dispatch(setStepEnum(stepToShow));
        this.dispatch(setShowFitAssistant(false));

        trackFit(
          this.initialData.FitAssistantVariation,
          this.initialData.IsYearFirst,
          this.selectedCar,
          this.shouldTriggerTracking
        );
      } else if (
        this.response.MatchingProducts?.length > 0 &&
        this.response.MatchingProducts?.filter((x) => x.CompatibleBarForCaprock)?.length > 0
      ) {
        this.dispatch(setStepEnum(FitAssistantStep.RoofRacks));
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }

      this.dispatch(setFitStatus(fitStatus));
      this.dispatch(setShowResetButtons(true));
    } else if (
      this.response.MatchingProducts?.length > 0 &&
      this.response.MatchingProducts?.filter((x) => x.CompatibleBarForCaprock)?.length > 0
    ) {
      if (this.selectedCarFromCookie?.getSliderList[4].data.find((x) => x.Selected)) {
        SetFitStatusForAllProducts(FitAssistantStatus.Confirmed);
        this.dispatch(setFitStatus(FitAssistantStatus.Confirmed));
        this.dispatch(setShowResetButtons(true));

        if (IsProductPage) {
          let currentStepData = this.stepData.find((x) => x.parameters === FitAssistantStep.RoofRacks);
          ProductPageHelper.HandleProductPageData(
            currentStepData,
            window._THULEDATA.ProductDetail,
            this.initialData,
            this.selectedCar,
            this.dispatch
          );
        }
      } else {
        this.dispatch(setShowResetButtons(false));
      }

      this.dispatch(setStepEnum(FitAssistantStep.RoofRacks));
    } else {
      const fitStatus: FitAssistantStatus = this.SetCompatibility();

      this.dispatch(setFitStatus(fitStatus));
      this.dispatch(setStepEnum(FitAssistantStep.NoFits));
    }
  }

  private HandleRooftopTents(): void {
    let selectedOption: StepDataData | undefined;

    let steps = this.selectedCar.filter(
      (x) => x.value !== '0' && x.parameterName !== Constants.Parameters.CONFIRM_COMPATIBILITY
    );
    let currentStep = steps[steps.length - 1];

    if (this.selectedCarFromCookie) {
      selectedOption = this.selectedCarFromCookie.getSliderList
        .find((x) => x.parameters === currentStep.parameterName)
        ?.data?.find((x) => x.Selected);
    } else {
      selectedOption = this.stepData
        .find((x) => x.parameters === currentStep.parameterName)
        ?.data?.find((x) => x.Selected);
    }

    this.dispatch(addMatchingProducts(this.data));

    if (
      selectedOption?.CompatibleRackForTent === FitAssistantRooftopTentStatus.Confirmed &&
      (this.selectedCar.find((x) => x.parameterName === Constants.Parameters.CONFIRM_COMPATIBILITY) ||
        !selectedOption?.StructurallySupported ||
        (selectedOption?.StructurallySupported as FitAssistantRooftopTentStatus) ===
          FitAssistantRooftopTentStatus.Confirmed)
    ) {
      const fitStatus: FitAssistantStatus = this.SetCompatibility();

      this.dispatch(setFitStatus(fitStatus));

      if (fitStatus === FitAssistantStatus.NoFit) {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        this.dispatch(setShowResetButtons(true));
      } else if (fitStatus === FitAssistantStatus.Confirmed) {
        let stepsWithValue = this.selectedCar.filter(
          (x) => x.value !== '0' && x.parameterName !== Constants.Parameters.CONFIRM_COMPATIBILITY
        );
        let stepToShow = stepsWithValue[stepsWithValue.length - 1].parameterName;

        this.dispatch(setStepEnum(stepToShow));
        this.dispatch(setShowFitAssistant(false));
        this.dispatch(setShowResetButtons(true));

        trackFit(
          this.initialData.FitAssistantVariation,
          this.initialData.IsYearFirst,
          this.selectedCar,
          this.shouldTriggerTracking
        );
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
      }
    } else {
      this.dispatch(setStepEnum(FitAssistantStep.RackNotCompatible));
      this.dispatch(setFitStatus(FitAssistantStatus.NoFit));
      SetFitStatusForAllProducts(FitAssistantStatus.NoFit);
    }
  }

  private HandleCarSeats(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.CarSeatFit));
      this.dispatch(setShowFitAssistant(false));

      trackFit(
        this.initialData.FitAssistantVariation,
        this.initialData.IsYearFirst,
        this.selectedCar,
        this.shouldTriggerTracking
      );
    }
    this.dispatch(setShowResetButtons(true));
  }

  private HandleDefault(): void {
    const fitStatus: FitAssistantStatus = this.SetCompatibility();

    this.dispatch(addMatchingProducts(this.data));
    this.dispatch(setFitStatus(fitStatus));

    if (fitStatus === FitAssistantStatus.NoFit) {
      if (this.response.MatchingProducts?.length > 0) {
        if (IsProductPage) {
          this.dispatch(setStepEnum(FitAssistantStep.OtherFits));
        } else {
          this.dispatch(setStepEnum(FitAssistantStep.NoFits));
        }
      } else {
        this.dispatch(setStepEnum(FitAssistantStep.NoFits));
      }
    }

    if (fitStatus === FitAssistantStatus.Unconfirmed) {
      this.dispatch(setStepEnum(FitAssistantStep.Unconfirmed));
    }

    if (fitStatus === FitAssistantStatus.Confirmed) {
      let stepsWithValue = this.selectedCar.filter((x) => x.value !== '0');
      let stepToShow = stepsWithValue[stepsWithValue.length - 1].parameterName;

      this.dispatch(setStepEnum(stepToShow));
      this.dispatch(setShowFitAssistant(false));

      trackFit(
        this.initialData.FitAssistantVariation,
        this.initialData.IsYearFirst,
        this.selectedCar,
        this.shouldTriggerTracking
      );
    }

    this.dispatch(setShowResetButtons(true));
  }

  private SetCompatibility(): FitAssistantStatus {
    if (!this.response || !this.response.Success) return FitAssistantStatus.Unconfirmed;

    let confirmedCards: HTMLDivElement[] = [];
    let fitStatus = FitAssistantStatus.NoFit;

    if (this.response.SolutionComponents) {
      handleDocuments(this.response.SolutionComponents);
    }

    if (this.setCookie) {
      const selection = getSearchString(this.selectedCar);
      const perCategorySelection = getPerCategorySearchString(this.selectedCar);
      CookieHelper.SetCookies(selection, perCategorySelection);

      onFitAssistantSaveData(this.selectedCar);

      if (IsProductPage) setQueryFromSearchParameters(this.initialData.FitAssistantCookieName, this.selectedCar);
    }

    if (IsProductPage) {
      fitStatus = this.setCompatibilityForProductPage();
    } else {
      fitStatus = this.setCompatibilityForProductProductListing(confirmedCards);
    }

    return fitStatus;
  }

  private setCompatibilityForProductPage(): FitAssistantStatus {
    let solutionIdsElement: HTMLInputElement = document.querySelector('.product-ids-for-solution') as HTMLInputElement;
    let productIdElement: HTMLInputElement = document.querySelector('.product-id') as HTMLInputElement;
    let buyAreaElement: HTMLDivElement = document.querySelector('#productBuyArea') as HTMLDivElement;

    let productId = productIdElement?.value ?? buyAreaElement.dataset.productid;

    let fitStatus = this.response.ProductPageMatch
      ? FitAssistantStatus.Confirmed
      : isConfirmedNoFit(this.response.NoFitIds, productId)
        ? FitAssistantStatus.NoFit
        : this.initialData.DisableUnconfirmed
          ? FitAssistantStatus.NoFit
          : FitAssistantStatus.Unconfirmed;

    if (solutionIdsElement) {
      let bundleIds = solutionIdsElement.dataset.defaultids;

      for (let product of this.response.MatchingProducts) {
        if (bundleIds?.includes(product.PimId)) {
          fitStatus = FitAssistantStatus.Confirmed;
          break;
        }
      }
    }

    if (
      window._THULEDATA?.isExtranet &&
      window._THULEDATA.FitAssistant.FitAssistantVariation != FitAssistantVariation.RooftopTents
    ) {
      UpdateFitAssistantPriceAndATCForPDPExtranet(
        this.response.SolutionComponents,
        this.response.SolutionPriceContainer
      );
    } else {
      UpdateFitAssistantPriceAndATCForPDP(this.response.SolutionPriceContainer, this.response.AddToCartButton);
    }

    setImageBasedOnRoofRack(this.response);

    return fitStatus;
  }

  private setCompatibilityForProductProductListing(confirmedCards: HTMLDivElement[]): FitAssistantStatus {
    let matchingPimIds = this.response.MatchingProducts.map((x) => {
      return x.PimId;
    });

    let allPimIds: string[] = [];
    let cards: NodeListOf<HTMLDivElement> = document.querySelectorAll('div.isotope-item[data-pimid]');
    cards.forEach((card) => {
      let pimId: string = card.getAttribute('data-pimid') as string;
      let matchingProduct: MatchingProduct;

      if (pimId) allPimIds.push(pimId);

      if (pimId && matchingPimIds.indexOf(pimId) > -1) {
        confirmedCards.push(card);
        matchingProduct = this.response.MatchingProducts.find((x) => x.PimId === pimId);

        setPricePLP(card, matchingProduct?.SolutionPriceContainer);
        setAddToCartBtnForDBG(card, matchingProduct);
      } else if (
        !IsProductPage &&
        window._THULEDATA.FitAssistant.FitAssistantVariation == FitAssistantVariation.CarSeats
      ) {
        const bundleIds: string = card.getAttribute('data-bundle-ids') as string;
        if (bundleIds) {
          let bundleIdArray: string[] = bundleIds.split(',');
          let intersection: string[] = bundleIdArray.filter((bundle) => matchingPimIds.includes(bundle));

          if (intersection.length > 0) confirmedCards.push(card);
        }

        setSolutionProductListItemImage(card, matchingProduct);
      }
    });

    if (confirmedCards.length > 0) {
      SetFitStatusForConfirmedFits(confirmedCards, this.response.NoFitIds, this.initialData);
      return FitAssistantStatus.Confirmed;
    } else if (
      confirmedCards.length === 0 &&
      (this.response.NoFitIds.length === 0 || !allPimIds.every((x) => this.response.NoFitIds.includes(x))) &&
      !this.initialData.DisableUnconfirmed
    ) {
      return FitAssistantStatus.Unconfirmed;
    } else {
      return FitAssistantStatus.NoFit;
    }
  }
}
